/**
 * Partner landing hits (2026-10-13).
 *
 * Some partners are paid per CLICK on a landing of ours, e.g. Facile sends its
 * visitors to https://life5.it/facile/?externalReference=FIT-<uuid>, one id per
 * click. Those clicks used to be counted only in PostHog ($pageview); the CRM
 * now keeps its own counter, idempotent per click id, and this module tells it
 * about every click that lands.
 *
 * Deliberately minimal: no cookies, no storage, no visitor id and no consent
 * gating. It is billing measurement and the only identifier sent is the click
 * id the partner put in the url. The body travels as a plain string
 * (text/plain), which is CORS-safelisted: no preflight, no credentials, and the
 * response is never read.
 */

export interface PartnerLanding {
  partner: string;
  landing: string;
  /** Query param carrying the partner's click id (matched case-insensitively). */
  clickParam: string;
}

/** Keyed by pathname WITH trailing slash (gatsby-config `trailingSlash: "always"`). */
export const PARTNER_LANDINGS: Record<string, PartnerLanding> = {
  "/facile/": { partner: "facile", landing: "showcase", clickParam: "externalReference" },
};

export interface PartnerLandingHit {
  partner: string;
  landing: string;
  clickId: string;
  path: string;
}

const CRM_BASE_URL_PRD = "https://crm.life5.com";
const CRM_BASE_URL_TEST = "https://crm.test.life5.com";
export const LANDING_HITS_PATH = "/api/v1/public/landing-hits";

// One beacon per click id and page load: onRouteUpdate fires again on every
// client-side navigation and the url may still carry the param.
const sentClickIds = new Set<string>();

const normalizePath = (pathname: string): string =>
  pathname.endsWith("/") ? pathname : `${pathname}/`;

/** Exact key first, then any casing (`externalreference`, `ExternalReference`…). */
export const readQueryParam = (search: string, name: string): string | null => {
  const query = new URLSearchParams(search);
  let value = query.get(name);
  if (value === null) {
    const wanted = name.toLowerCase();
    query.forEach((v, key) => {
      if (value === null && key.toLowerCase() === wanted) value = v;
    });
  }
  const trimmed = (value || "").trim();
  return trimmed ? trimmed : null;
};

/** The hit to report for this url, or null when it is not a partner click. */
export const buildPartnerLandingHit = (
  pathname: string,
  search: string
): PartnerLandingHit | null => {
  const path = normalizePath(pathname || "");
  const landing = PARTNER_LANDINGS[path];
  if (!landing) return null;
  const clickId = readQueryParam(search || "", landing.clickParam);
  if (!clickId) return null;
  return { partner: landing.partner, landing: landing.landing, clickId, path };
};

/**
 * CRM the beacon goes to. `GATSBY_CRM_BASE_URL` wins when set (also the only
 * way to send from a local `gatsby develop`). Otherwise it follows the host the
 * page is served from — the same host the browser sends as Origin: a `test`
 * label (test.life5.it) is TEST, any other life5 domain (life5.it) is PRD, and
 * anything else (localhost, previews) sends nothing.
 */
export const crmBaseUrlFor = (
  hostname: string,
  override: string | undefined = process.env.GATSBY_CRM_BASE_URL
): string | null => {
  const explicit = (override || "").trim().replace(/\/+$/, "");
  if (explicit) return explicit;
  const host = (hostname || "").toLowerCase();
  if (!/(^|\.)life5\.[a-z]+$/.test(host)) return null;
  return host.split(".").includes("test") ? CRM_BASE_URL_TEST : CRM_BASE_URL_PRD;
};

const postHit = (url: string, body: string): void => {
  let queued = false;
  try {
    queued =
      typeof navigator !== "undefined" &&
      typeof navigator.sendBeacon === "function" &&
      navigator.sendBeacon(url, body);
  } catch (error) {
    queued = false;
  }
  if (queued || typeof fetch !== "function") return;
  fetch(url, {
    method: "POST",
    body,
    keepalive: true,
    mode: "no-cors",
    credentials: "omit",
    headers: { "Content-Type": "text/plain;charset=UTF-8" },
  }).catch(() => {});
};

/** Called from gatsby-browser `onRouteUpdate`. Never throws. */
export const sendPartnerLandingHit = (location?: {
  pathname?: string;
  search?: string;
}): void => {
  try {
    if (typeof window === "undefined") return;
    const loc = location || window.location;
    const hit = buildPartnerLandingHit(loc.pathname || "", loc.search || "");
    if (!hit) return;
    const key = `${hit.partner}:${hit.clickId}`;
    if (sentClickIds.has(key)) return;
    const baseUrl = crmBaseUrlFor(window.location.hostname);
    if (!baseUrl) return;
    sentClickIds.add(key);
    postHit(`${baseUrl}${LANDING_HITS_PATH}`, JSON.stringify(hit));
  } catch (error) {
    // Measurement only: the page must never notice.
  }
};
